// @flow

import React, {useContext, useState} from 'react';
import TextInput from '../GUI_COMMON/COMPONENTS/TextInput';
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import LocationSelector from "./LocationSelector";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import BoxesList, {BoxesListWillReturnNothing} from "./BoxesList";
import ContactFind from "./ContactFind";
import LIButton from "../GUI_COMMON/COMPONENTS/LIButton";
import type {BTOBoxAndMagsItem, BTOInfoItem} from "./BTOAddUpdatePage"; 
import {BTO_GHOSTBOX_BARCODE} from "./BTOPickSheetPage";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import HelpWithOnClick from "../GUI_COMMON/COMPONENTS/HelpWithOnClick";
import NotesEditor from "./NotesEditor";

export interface BTOOrderFormProps { 
    btoInfo: BTOInfoItem | null,
    onChange: ()=>void,
    onSubmit: ()=>void,
    onAddBoxes: ()=>void,
    onBoxClicked: (BTOBoxAndMagsItem)=>void,
    onBoxOrderClicked: (btoOrderId:number)=>void,
    onBoxBadgeClicked: (box: BTOBoxAndMagsItem, badgeText: string)=>void,
    onRemoveBox: ()=>void,
    formIsValid: boolean,
    isSaving: boolean,
    formIsReadOnly: boolean,
    isDirty: boolean,
    isLoadingBoxes: boolean,
    onAddNoteClicked?: (newNote:string)=>void,
    newlyAddedBoxIds?: Set<number>
}
const BTOOrderForm : React.FC<BTOOrderFormProps> = ({
                                                        btoInfo, onChange, onSubmit, onAddBoxes, 
                                                        onBoxClicked, onBoxOrderClicked, onBoxBadgeClicked,
                                                        onRemoveBox, formIsValid, isSaving, formIsReadOnly, 
                                                        isDirty, isLoadingBoxes, onAddNoteClicked, newlyAddedBoxIds}) => {
    
    const [globalState] = useContext(StoreContext);
    const [boxAddRangeValue,setBoxAddRangeValue] = useState("");
    const [locationIsValid, setLocationIsValid] = useState(false);      // set to true if the location component is satisfied with the current selection
    
    if(formIsReadOnly===true) {  // define some default empty functions to avoid browser errors when the form is in readonly mode.
        onChange=() =>{};
        onAddBoxes=()=>{};
    }
    
    const isNewOrder = () => {
        return btoInfo?.orderId==="" || btoInfo?.orderId === -1
    }
    
    const onLocationIsValidChanged = (isValid) => {
        console.log(`*location* isValid=${isValid}`);
        setLocationIsValid(isValid);
    }

    const dumpBoxList = () => {
        let someBoxesAreAvailable=false;
        let magListMap = globalState.magTypes.items.map( (type) => {
            if(BoxesListWillReturnNothing(btoInfo?.boxesAndChildMags??[],type.id)) return null;
            someBoxesAreAvailable=true;
            return (
                <div key={"magType_"+type.id} id={"magList-"+type.id}>
                    <hr className="li-bg-dark"/>
                    <h3 className="li-header-row li-border-muted li-bg-white p-1">{type.description}</h3>
                    <BoxesList name={type.id+"-cb"} boxesAndMags={btoInfo.boxesAndChildMags??[]} magType={type.id}
                               onBoxClicked={onBoxClicked}
                               onOrderClicked={onBoxOrderClicked} 
                               onBoxBadgeClicked={onBoxBadgeClicked}
                               showDelete 
                               onDeleteClicked={onRemoveBox}
                               readOnly={formIsReadOnly}
                               thisOrderId={btoInfo.orderId} returnNullOnNoneFound
                               isLoading={isLoadingBoxes}
                               newlyAddedBoxIds={newlyAddedBoxIds}/>
                </div>);
        });
        if(!someBoxesAreAvailable) {
            return <div className="p-2">No boxes added to this order yet</div>;
        }
        return magListMap;
    }
    
    const showMissingBoxesHelp = () => {
        Popup.show("Missing Boxes?","<p>Boxes were not found in the system for these mags.</p>" + 
                            "<p>You should note the boxes for the listed magazines, remove them from the order, " +
                            "add then add their boxes using the 'Create Boxes' button on the Admin/Services page.</p>"+
                            "<p>Then you can re-add those boxes to the order.</p>");    
    }
    
    const dumpGhostBoxIfPresent = () => {
        // a single ghost box is present in the box list
        const ghostBox = btoInfo?.boxesAndChildMags.find( (boxAndMags) => {
            return boxAndMags.box.barcode===BTO_GHOSTBOX_BARCODE
        })
        if(ghostBox) {
            return <>
                <div className="mt-4 li-slideblock-list">
                    <div className="li-font-medium font-weight-bold">
                        <i className="fa fa-exclamation-triangle li-fg-danger mr-2"/>Mags Without Boxes
                        <span className="mr-2"/>
                        <HelpWithOnClick onClick={showMissingBoxesHelp} small/>
                    </div>
                    <div key={"magType_ghost"} id={"magList-ghost"}>
                        <hr className="li-bg-dark"/>
                        <BoxesList name={"ghost-cb"} boxesAndMags={[ghostBox]} magType={0} 
                                   onBoxClicked={onBoxClicked}
                                   onOrderClicked={onBoxOrderClicked}
                                   onBoxBadgeClicked={formIsReadOnly?null:onBoxBadgeClicked}
                                   showDelete
                                   onDeleteClicked={onRemoveBox}
                                   readOnly={formIsReadOnly}
                                   thisOrderId={btoInfo.orderId} returnNullOnNoneFound
                                   isLoading={isLoadingBoxes}
                                   newlyAddedBoxIds={newlyAddedBoxIds}/>
                    </div>
                </div>
            </>;
        }
    }

    const onBoxAddRangeChanged = (e) => {
        const boxRangeValue = e.target.value;
        setBoxAddRangeValue(boxRangeValue);
    }

    function handleClearFilterClicked() {
        const el = document.getElementById("addBoxesTB");
        el.value="";
        onBoxAddRangeChanged({target:{value:""}});
    }

    function handleLocationHelpClicked() {
        Popup.show("Locations",
            "<p>Selecting a location that is \"Off Premises\" will result in any samples associated with this order being considered as \"Out of this system\".</p>"
            + "<p>Off-premises samples are not available for picking on Retrieval Orders.</p>");
    }
    
    return (
        <form id="btoOrderForm">
            {formIsReadOnly && <h6 className="text-right li-fg-slightly-muted">
                <i className="fa fa-exclamation-circle"/> Form is read-only.</h6>}
            <div className="">
                <div>
                    <div className="li-form-label m-0 p-0">Created: <span className="li-fg-slightly-muted">
                        {(btoInfo?.createdOn)?LIUtils.dateToShortDateTimeString(btoInfo?.createdOn):" (now)"}</span>
                    </div>
                </div>
                <div className="row">
                    <div className="col-5 pl-0">
                        <TextInput name="createdByName" label="Created By" readOnly onChange={onChange}
                                   placeholder="" tabIndex={0}
                                   value={btoInfo?.createdByName??""}
                                   required/>
                    </div>
                    <div className="col-7 offset-0">
                       <ContactFind widgetName="requestedBy" onContactSelected={onChange} tabIndex={1}
                                    initialValue={
                                        {   
                                            name:btoInfo?.requestedByName??"",
                                            id:btoInfo?.requestedById??-1,
                                        }
                                    }
                                    required readOnly={formIsReadOnly}/>
                    </div>
                </div>
                
               <LocationSelector name="bto" onChange={onChange} tabIndex={2}
                                 locationValue={btoInfo?.destLocation??""} 
                                 departmentValue={btoInfo?.destDepartment??""} 
                                 withDepartmentsOnly={false} 
                                 showContact={false}
                                 onIsValid={onLocationIsValidChanged} 
                                 showOffPremises={true}
                                 onHelpClicked={handleLocationHelpClicked}
                                 required 
                                 readOnly={formIsReadOnly}/>
            </div>

            <NotesEditor name="notes" onChange={onChange} value={btoInfo?.notes??""} showAddButton={formIsReadOnly}
                         rows={3} tabIndex={5} onAddNoteClicked={onAddNoteClicked} readOnly={formIsReadOnly}/>
            
            <div className="card">
                <div className="card-body li-bg-light">
                    <div className="row justify-content-start pl-2">
                        <div className="li-form-label mt-0 mr-2">Add Boxes</div>
                            <div className="input-group">
                                <TextInput name="addBoxesTB" label="" onChange={onBoxAddRangeChanged} tabIndex={6}
                                           placeholder="YY?.####" onReturnPressed={()=>onAddBoxes(boxAddRangeValue)}
                                           readOnly={formIsReadOnly || isLoadingBoxes} widthPels={300}/>
                                <div className="input-group-append">
                                    <LIButton bgClass="ml-2" faIcon="fa fa-magnifying-glass" small primary tabIndex={7}
                                              onClick={()=>onAddBoxes(boxAddRangeValue)} showBusy={isLoadingBoxes}  disabled={formIsReadOnly || isLoadingBoxes || !boxAddRangeValue}/>
                                </div>
                                <div className="input-group-append">
                                    <LIButton bgClass="ml-2" faIcon="fa fa-times-circle" small secondary tabIndex={8}
                                              onClick={handleClearFilterClicked} showBusy={isLoadingBoxes}/>
                                </div>
                            </div>
                        <medium className="d-block text-muted">* use "24M.0001-24M.0010" type format for ranges</medium>
                    </div>
                </div>
            </div>
            
            <div className="mt-4 li-slideblock-list">
                <div className="li-font-medium font-weight-bold">Boxes On This Order</div>
                {dumpBoxList()}
            </div>
            
            {dumpGhostBoxIfPresent()}
            
            <div className="row justify-content-center mt-2">
                {
                    !formIsReadOnly &&
                        <button type="button" id="addUpdateOrderBtn" className="li-button li-primary li-font-large px-5 mt-2 mb-3" 
                                onClick={onSubmit} tabIndex={8}
                                disabled={!(formIsValid && locationIsValid && isDirty) || isLoadingBoxes || isSaving}>
                            {isSaving?<LoadingSpinner small/> : isNewOrder()?"Add Order":"Update Order"}</button>
                }
            </div>
        </form>
    );
};

export default BTOOrderForm;